/**
 * Champ SIRET assisté (lot 1.1) : on tape un SIRET, un SIREN ou le nom de l'entreprise, l'annuaire officiel de l'État
 * propose les établissements correspondants, un clic remplit la fiche (raison sociale, adresse…).
 *
 * Accessibilité (WCAG 2.2) : le champ reste un champ texte ordinaire, toujours saisissable à la main ; les suggestions
 * sont de vrais boutons (cibles d'au moins 44 px), annoncées par une zone `status` ; aucune information n'est donnée
 * par la couleur seule. 3.3.7 « saisie redondante » : on évite de retaper ce que l'annuaire connaît déjà.
 * Si l'annuaire est indisponible, un message le dit et rien n'est bloqué (repli gracieux, côté serveur aussi).
 */
import { useEffect, useId, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Building2, Check, Loader2 } from "lucide-react";
import { analyseSaisie, formateSiret } from "@/domaine/entreprises/siret";
import type { EntrepriseTrouvee } from "@/domaine/entreprises/annuaire";
import { api } from "../api";
import { Champ, cx } from "./base";

type Resultat = { ok: true; resultats: EntrepriseTrouvee[] } | { ok: false; message: string };

function useDiffere<T>(valeur: T, ms: number): T {
  const [v, setV] = useState(valeur);
  useEffect(() => {
    const t = setTimeout(() => setV(valeur), ms);
    return () => clearTimeout(t);
  }, [valeur, ms]);
  return v;
}

export function ChampSiret({
  libelle = "SIRET",
  value,
  onChange,
  onChoisir,
  erreur,
  aide = "14 chiffres, ou le nom de l'entreprise : la fiche se remplit depuis l'annuaire officiel des entreprises.",
  required,
  disabled,
  id,
  placeholder = "SIRET ou nom de l'entreprise",
}: {
  libelle?: string;
  value: string;
  onChange: (valeur: string) => void;
  /** Appelé quand l'utilisateur choisit une suggestion : à l'écran de remplir les autres champs (sans écraser une saisie). */
  onChoisir: (e: EntrepriseTrouvee) => void;
  erreur?: string;
  aide?: string;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
}) {
  const statutId = useId();
  const [choisi, setChoisi] = useState<EntrepriseTrouvee | null>(null);
  const saisie = useDiffere(value, 350);
  const analyse = analyseSaisie(saisie);
  const aChercher = !disabled && (analyse.type === "siret" || analyse.type === "siren" || analyse.type === "nom") && choisi?.siret !== saisie.replace(/\D/g, "");
  const recherche = useQuery({
    queryKey: ["annuaire-entreprises", analyse.type, "q" in analyse ? analyse.q : ""],
    queryFn: () => api.get<Resultat>(`/annuaire-entreprises?q=${encodeURIComponent("q" in analyse ? analyse.q : "")}`),
    enabled: aChercher,
    staleTime: 10 * 60_000,
    retry: false,
  });
  // Revue I-2 : un nom (ou un SIREN) sert à CHERCHER, jamais à être enregistré comme SIRET. Dès que l'utilisateur quitte
  // le champ sans avoir choisi d'entreprise, on le lui dit (le serveur refuse aussi : `schemaSiret`).
  const [quitte, setQuitte] = useState(false);
  const analyseValeur = analyseSaisie(value);
  const invalide =
    analyseValeur.type === "invalide"
      ? analyseValeur.message
      : quitte && (analyseValeur.type === "nom" || analyseValeur.type === "siren")
        ? "Choisissez l'entreprise dans la liste, ou saisissez les 14 chiffres du SIRET : un nom ou un SIREN ne peut pas être enregistré ici."
        : undefined;

  const choisir = (e: EntrepriseTrouvee) => {
    setChoisi(e);
    onChange(e.siret);
    onChoisir(e);
  };

  const r = aChercher ? recherche.data : undefined;
  let statut: React.ReactNode = null;
  if (choisi && choisi.siret === value.replace(/\D/g, "")) {
    statut = (
      <span className="inline-flex items-start gap-1.5 text-valide">
        <Check className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <span>
          Fiche remplie depuis l'annuaire officiel : {choisi.raison_sociale}. Vérifiez les champs avant d'enregistrer.
        </span>
      </span>
    );
  } else if (aChercher && recherche.isFetching) {
    statut = (
      <span className="inline-flex items-center gap-1.5">
        <Loader2 className="size-3.5 tourne" aria-hidden /> Recherche dans l'annuaire des entreprises…
      </span>
    );
  } else if (aChercher && recherche.error) {
    statut = "Recherche indisponible pour le moment : saisissez les informations à la main.";
  } else if (r && !r.ok) {
    statut = r.message;
  } else if (r?.ok) {
    statut = r.resultats.length === 0 ? "Aucune entreprise trouvée : vérifiez la saisie, ou complétez la fiche à la main." : r.resultats.length === 1 ? "1 entreprise trouvée : choisissez-la pour remplir la fiche." : `${r.resultats.length} entreprises trouvées : choisissez la bonne pour remplir la fiche.`;
  }

  return (
    <div className="min-w-0">
      <Champ libelle={libelle} id={id} value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => setQuitte(false)} onBlur={() => setQuitte(true)} erreur={erreur ?? invalide} aide={aide} required={required} disabled={disabled} autoComplete="off" spellCheck={false} placeholder={placeholder} maxLength={120} aria-controls={statutId} />
      <p id={statutId} role="status" aria-live="polite" className="mt-1.5 text-[13px] text-encre-2 empty:hidden">
        {statut}
      </p>
      {r?.ok && r.resultats.length > 0 && !(choisi && choisi.siret === value.replace(/\D/g, "")) && (
        <ul aria-label="Entreprises trouvées dans l'annuaire" className="mt-2 divide-y divide-trait overflow-hidden rounded-md border border-trait-fort bg-carte shadow-carte">
          {r.resultats.map((e) => (
            <li key={e.siret}>
              <button type="button" onClick={() => choisir(e)} className="group flex min-h-11 w-full items-start gap-3 px-3 py-2.5 text-left transition-colors duration-150 hover:bg-papier-2 focus-visible:bg-papier-2">
                <Building2 className="mt-0.5 size-4 shrink-0 text-encre-3 group-hover:text-accent" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium break-words">
                    {e.raison_sociale}
                    {e.nom_commercial && <span className="font-normal text-encre-2"> — {e.nom_commercial}</span>}
                  </span>
                  <span className="block text-[12.5px] break-words text-encre-2">
                    <span className="chiffres">{formateSiret(e.siret)}</span> · {e.adresse || "adresse non diffusée"}
                    {e.siege ? " · siège" : ""}
                  </span>
                </span>
                {!e.actif && <span className="shrink-0 rounded-xs bg-danger-doux px-1.5 py-0.5 text-[11px] font-semibold text-danger-encre">Fermé</span>}
                <span className={cx("inline-flex shrink-0 items-center gap-1 self-center text-[12.5px] font-medium text-accent")}>
                  <span className="max-sm:sr-only">Utiliser</span>
                  <ArrowRight className="size-3.5" aria-hidden />
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
